export const CWJ_WORKFLOW_ENGINE_VERSION = "0.1.0";
export const DEFAULT_WORKFLOW_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
export const DEFAULT_MAX_EVIDENCE_BYTES = 64 * 1024; // 64 KiB
export const DEFAULT_MAX_EVIDENCE_COUNT_PER_STAGE = 10;
export const CWJ_WORKFLOW_STAGE_ORDER = [
    "intake",
    "product_review",
    "engineering_plan",
    "design_security_review",
    "approval",
    "implementation",
    "code_review",
    "qa",
    "release_evidence",
    "owner_approval",
    "retrospective",
];
export const CWJ_WORKFLOW_STAGE_DEFINITIONS = {
    intake: {
        stageId: "intake",
        title: "Intake",
        description: "Gather request, confirm scope and owner.",
        required: true,
        requiresApproval: false,
        approverRole: "none",
        evidenceTypes: ["request_doc"],
        skipAllowed: false,
        maxRetries: 0,
        timeoutMs: DEFAULT_WORKFLOW_TIMEOUT_MS,
        riskLevel: "low",
        nextStage: "product_review",
        failClosed: true,
    },
    product_review: {
        stageId: "product_review",
        title: "Product Review",
        description: "Product-level review of the request.",
        required: true,
        requiresApproval: false,
        approverRole: "none",
        evidenceTypes: ["product_review_checklist"],
        skipAllowed: false,
        maxRetries: 0,
        timeoutMs: DEFAULT_WORKFLOW_TIMEOUT_MS,
        riskLevel: "low",
        nextStage: "engineering_plan",
        failClosed: true,
    },
    engineering_plan: {
        stageId: "engineering_plan",
        title: "Engineering Plan",
        description: "Technical plan, architecture and task breakdown.",
        required: true,
        requiresApproval: false,
        approverRole: "none",
        evidenceTypes: ["engineering_plan_doc"],
        skipAllowed: false,
        maxRetries: 0,
        timeoutMs: DEFAULT_WORKFLOW_TIMEOUT_MS,
        riskLevel: "medium",
        nextStage: "design_security_review",
        failClosed: true,
    },
    design_security_review: {
        stageId: "design_security_review",
        title: "Design / Security Review",
        description: "Design and security risk review.",
        required: true,
        requiresApproval: false,
        approverRole: "none",
        evidenceTypes: ["design_review_checklist", "security_review_checklist"],
        skipAllowed: false,
        maxRetries: 0,
        timeoutMs: DEFAULT_WORKFLOW_TIMEOUT_MS,
        riskLevel: "medium",
        nextStage: "approval",
        failClosed: true,
    },
    approval: {
        stageId: "approval",
        title: "Approval Gate",
        description: "Owner approval to begin implementation.",
        required: true,
        requiresApproval: true,
        approverRole: "owner",
        evidenceTypes: [],
        skipAllowed: false,
        maxRetries: 0,
        timeoutMs: DEFAULT_WORKFLOW_TIMEOUT_MS,
        riskLevel: "high",
        nextStage: "implementation",
        failClosed: true,
    },
    implementation: {
        stageId: "implementation",
        title: "Implementation",
        description: "Code, tests, and evidence collection.",
        required: true,
        requiresApproval: false,
        approverRole: "none",
        evidenceTypes: ["implementation_summary", "test_results"],
        skipAllowed: false,
        maxRetries: 0,
        timeoutMs: DEFAULT_WORKFLOW_TIMEOUT_MS,
        riskLevel: "medium",
        nextStage: "code_review",
        failClosed: true,
    },
    code_review: {
        stageId: "code_review",
        title: "Code Review",
        description: "Peer review of implementation.",
        required: true,
        requiresApproval: false,
        approverRole: "none",
        evidenceTypes: ["code_review_checklist"],
        skipAllowed: false,
        maxRetries: 0,
        timeoutMs: DEFAULT_WORKFLOW_TIMEOUT_MS,
        riskLevel: "medium",
        nextStage: "qa",
        failClosed: true,
    },
    qa: {
        stageId: "qa",
        title: "QA",
        description: "Quality assurance and acceptance checks.",
        required: true,
        requiresApproval: false,
        approverRole: "none",
        evidenceTypes: ["qa_report"],
        skipAllowed: false,
        maxRetries: 1,
        timeoutMs: DEFAULT_WORKFLOW_TIMEOUT_MS,
        riskLevel: "high",
        nextStage: "release_evidence",
        failClosed: true,
    },
    release_evidence: {
        stageId: "release_evidence",
        title: "Release Evidence",
        description: "Collect release notes, version policy and rollback plan.",
        required: true,
        requiresApproval: false,
        approverRole: "none",
        evidenceTypes: ["release_notes", "version_policy_report", "rollback_plan"],
        skipAllowed: false,
        maxRetries: 0,
        timeoutMs: DEFAULT_WORKFLOW_TIMEOUT_MS,
        riskLevel: "high",
        nextStage: "owner_approval",
        failClosed: true,
    },
    owner_approval: {
        stageId: "owner_approval",
        title: "Owner Approval",
        description: "Final owner sign-off before release.",
        required: true,
        requiresApproval: true,
        approverRole: "owner",
        evidenceTypes: [],
        skipAllowed: false,
        maxRetries: 0,
        timeoutMs: DEFAULT_WORKFLOW_TIMEOUT_MS,
        riskLevel: "critical",
        nextStage: "retrospective",
        failClosed: true,
    },
    retrospective: {
        stageId: "retrospective",
        title: "Retrospective",
        description: "Post-release retrospective and lessons learned.",
        required: true,
        requiresApproval: false,
        approverRole: "none",
        evidenceTypes: ["retrospective_doc"],
        skipAllowed: false,
        maxRetries: 0,
        timeoutMs: DEFAULT_WORKFLOW_TIMEOUT_MS,
        riskLevel: "low",
        nextStage: undefined,
        failClosed: true,
    },
};
export const CWJ_WORKFLOW_RELEASE_STAGE_IDS = [
    "release_evidence",
    "owner_approval",
    "retrospective",
];
export function getStageDefinition(stageId) {
    return CWJ_WORKFLOW_STAGE_DEFINITIONS[stageId];
}
export function getNextStageId(stageId) {
    return getStageDefinition(stageId).nextStage;
}
/**
 * Maps workflow statuses to task-engine statuses for contract binding.
 * `undefined` means the task status is not representable as a workflow status.
 */
export function toCwjTaskStatus(status) {
    return status;
}
export function toCwjWorkflowStatus(status) {
    switch (status) {
        case "pending":
        case "running":
        case "paused":
        case "completed":
        case "failed":
        case "cancelled":
            return status;
        case "approval_required":
            return "pending";
        case "expired":
            return "failed";
        default:
            return undefined;
    }
}
//# sourceMappingURL=constants.js.map